import { computed, type Ref, toValue, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";

interface UseDraftLeadAuthCheckOptions {
    /** Draft lead token */
    token: Ref<string | undefined> | string | undefined;
    /** Whether the query is enabled */
    enabled?: Ref<boolean> | boolean;
    /** Whether to show toast on error (default: true) */
    showToast?: boolean;
}

/**
 * Composable for checking if a draft lead requires authentication before submission.
 * 
 * When `needsAuth` is true, the email in the draft belongs to an existing user
 * account, and the user must authenticate before submitting the draft.
 * 
 * When `needsAuth` is false, the email is new and the draft can be submitted
 * without authentication (a new account invitation will be sent).
 * 
 * @example
 * ```ts
 * const token = ref('abc123...');
 * const { needsAuth, isLoading, refetch } = useDraftLeadAuthCheck({ token });
 * 
 * // Before showing submit button, check if auth is needed
 * if (needsAuth.value) {
 *   // Show login form
 * } else {
 *   // Allow direct submission
 * }
 * ```
 */
export function useDraftLeadAuthCheck(options: UseDraftLeadAuthCheckOptions) {
    const api = useApi();
    const toaster = useToaster();

    const { token, enabled = true, showToast = true } = options;

    const tokenValue = computed(() => toValue(token));
    const enabledValue = computed(() => toValue(enabled) && !!tokenValue.value);

    const {
        data,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: computed(() => ["public", "draft-leads", tokenValue.value, "check-auth"]),
        queryFn: async () => {
            if (!tokenValue.value) {
                throw new Error("Draft lead token is required");
            }
            return await api.public.draftLeads.checkAuth({
                params: { token: tokenValue.value },
            });
        },
        enabled: enabledValue,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        staleTime: 60 * 1000, // 1 minute
        gcTime: 5 * 60 * 1000,
    });

    /**
     * Whether the email in the draft belongs to an existing user account.
     * If true, the user must authenticate before submission.
     */
    const needsAuth = computed(() => data.value?.needs_auth ?? false);

    watch(error, (err) => {
        if (err) {
            if (showToast) {
                toaster.create({
                    title: "Failed to check authentication",
                    description: "Unable to verify account status. Please try again.",
                    type: "error",
                });
            }
            console.error("[useDraftLeadAuthCheck] Error:", err);
        }
    });

    return {
        needsAuth,
        isLoading,
        error,
        refetch,
        data,
    };
}
